import { useEffect, useState } from 'react';
import { adviserBillingApi, type PracticeBillingObligations } from '../../advisers/api';

const date = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : 'Awaiting billing confirmation';

export default function AdviserObligationsPanel({ practiceId }: { practiceId: string }) {
  const [data, setData] = useState<PracticeBillingObligations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setData(null);
    setError('');
    adviserBillingApi
      .obligations(practiceId)
      .then((next) => {
        if (active) setData(next);
      })
      .catch((err) => {
        if (active)
          setError(err instanceof Error ? err.message : 'Unable to load firm payment commitments.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [practiceId, retry]);
  return (
    <section className="adviser-obligations" aria-labelledby="firm-obligations-title">
      <div className="adviser-section-heading">
        <div>
          <h2 id="firm-obligations-title">Firm payment commitments</h2>
          <p>
            Your subscriptions and authorised payment changes remain visible after client access
            ends.
          </p>
        </div>
      </div>
      {loading ? (
        <p role="status" className="adviser-muted">
          Loading firm payment commitments…
        </p>
      ) : error ? (
        <div className="adviser-error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="adviser-button adviser-button-secondary"
            onClick={() => setRetry((current) => current + 1)}
          >
            Retry commitments
          </button>
        </div>
      ) : data?.obligations.length ? (
        <div className="adviser-client-list">
          {data.obligations.map((item) => {
            const incoming =
              item.pendingHandover?.targetPayerType === 'practice' &&
              item.pendingHandover.targetPracticeId === practiceId;
            return (
              <article className="adviser-billing-component" key={item.id}>
                <div className="adviser-section-heading">
                  <div>
                    <h3>{item.component === 'planning' ? 'Company planning' : 'Pro execution'}</h3>
                    <p className="adviser-obligation-reference">
                      Company reference: {item.companyId}
                    </p>
                  </div>
                  <span className="adviser-badge">
                    {item.isCurrentPayer
                      ? 'Firm currently pays'
                      : incoming
                        ? 'Firm payment authorised'
                        : 'Payment commitment'}
                  </span>
                </div>
                <div className="adviser-billing-facts">
                  <div>
                    <span>Subscription</span>
                    <strong>
                      {item.quantity}{' '}
                      {item.component === 'planning' ? 'company plan' : 'client seats'} ·{' '}
                      {item.status.replace(/_/g, ' ')}
                    </strong>
                  </div>
                  <div>
                    <span>Next renewal</span>
                    <strong>{date(item.renewsAt)}</strong>
                  </div>
                </div>
                <p className="adviser-muted">
                  {item.delegationStatus === 'revoked' || item.delegationStatus === 'none'
                    ? 'Client access ended'
                    : item.delegationStatus === 'pending'
                      ? 'Awaiting client approval'
                      : 'Client collaboration active'}
                </p>
                {item.pendingHandover && (
                  <div className="adviser-notice">
                    <strong>
                      {incoming
                        ? 'Payment change to this firm'
                        : item.pendingHandover.targetPayerType === 'client'
                          ? 'Payment change to the client'
                          : 'Payment change to another adviser'}{' '}
                      · {item.pendingHandover.status.replace(/_/g, ' ')}
                    </strong>
                    <p>Effective {date(item.pendingHandover.effectiveAt)} for this component.</p>
                    {item.pendingHandover.error && <p role="alert">{item.pendingHandover.error}</p>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <p className="adviser-muted">No current firm payment commitments.</p>
      )}
    </section>
  );
}
